import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getToken, saveToken, setUnauthorizedHandler } from "@/lib/api";
import { queryClient } from "@/lib/query-client";

const AuthContext = createContext({
  user: null,
  loading: true,
  signInWithToken: async () => {},
  refresh: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      const me = await api("/auth/me");
      setUser(me);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    refresh().finally(() => setLoading(false));
    return () => setUnauthorizedHandler(null);
  }, [refresh]);

  const signInWithToken = useCallback(async (token, u) => {
    saveToken(token);
    queryClient.clear();
    setUser(u);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {
      /* ignore */
    }
    saveToken(null);
    queryClient.clear();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signInWithToken, refresh, signOut }),
    [user, loading, signInWithToken, refresh, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
